import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PrimaryButton, SecondaryButton } from '@/lib';
import { useApiClients } from '@/app/ApiProvider';

export type ImpersonateModalProps = {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
};

// Impersonation flow — PR-16. Confirm modal asks for read/write mode +
// duration; on submit, calls /v1/admin/tenants/{id}/impersonate,
// stashes the original token in sessionStorage, and replaces the
// active token via a tiny side-channel (window.dispatchEvent). The
// AppShell ImpersonationBanner picks up the swap and shows a
// countdown + End session button.
const TOKEN_OVERRIDE_KEY = 'pdm.impersonation';
const ORIGINAL_TOKEN_KEY = 'pdm.impersonation.original';

export type ImpersonationSession = {
  token: string;
  mode: 'read' | 'write';
  expiresAt: string;
  tenantName: string;
};

export function readImpersonationSession(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(TOKEN_OVERRIDE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationSession;
  } catch {
    return null;
  }
}

export function endImpersonation() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(TOKEN_OVERRIDE_KEY);
  window.sessionStorage.removeItem(ORIGINAL_TOKEN_KEY);
  window.dispatchEvent(new Event('pdm:impersonation-changed'));
  // Hard-reload so every component re-reads token state.
  window.location.assign('/radiant/tenants');
}

export function ImpersonateModal({ tenantId, tenantName, onClose }: ImpersonateModalProps) {
  const clients = useApiClients();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'read' | 'write'>('read');
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: apiErr } = await clients.apiGateway.POST(
        '/v1/admin/tenants/{tenantId}/impersonate',
        {
          params: { path: { tenantId } },
          body: { mode, duration_minutes: durationMinutes },
        },
      );
      if (apiErr || !data) throw new Error('impersonate failed');
      const original =
        window.localStorage.getItem('pdm.dev.token') ?? '';
      window.sessionStorage.setItem(ORIGINAL_TOKEN_KEY, original);
      window.sessionStorage.setItem(
        TOKEN_OVERRIDE_KEY,
        JSON.stringify({
          token: data.token,
          mode: data.mode,
          expiresAt: data.expires_at,
          tenantName,
        } satisfies ImpersonationSession),
      );
      window.dispatchEvent(new Event('pdm:impersonation-changed'));
      onClose();
      navigate({ to: '/' });
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="impersonate-heading"
      className="fixed inset-0 z-30 flex items-center justify-center bg-gray-900/40 p-sp-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex w-full max-w-md flex-col gap-sp-4 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6">
        <h2 id="impersonate-heading" className="text-fs-statement font-medium text-gray-900">
          Impersonate {tenantName}
        </h2>
        <p className="text-fs-meta text-gray-700">
          Read mode lets you see exactly what the customer's admin sees.
          Write mode also lets you mutate — audit log records both your
          Radiant identity and the on-behalf-of tenant admin.
        </p>
        <fieldset className="flex flex-col gap-sp-2">
          <legend className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Mode
          </legend>
          {(['read', 'write'] as const).map((m) => (
            <label key={m} className="inline-flex items-center gap-sp-2 text-fs-body text-gray-900">
              <input
                type="radio"
                name="mode"
                checked={mode === m}
                onChange={() => setMode(m)}
              />
              {m}
            </label>
          ))}
        </fieldset>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Duration · {durationMinutes} min
          <input
            type="range"
            min={1}
            max={60}
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value))}
          />
        </label>
        <div className="flex items-center gap-sp-3">
          <PrimaryButton
            label={submitting ? 'Starting…' : 'Confirm impersonation'}
            onClick={onConfirm}
            disabled={submitting}
          />
          <SecondaryButton label="Cancel" onClick={onClose} />
          {error ? (
            <span role="alert" className="text-fs-meta text-critical">
              {error}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
